import { hasPermission } from './permission'

const hasAnyPermission = (permissions: string[]): boolean =>
  permissions.some((permission) => hasPermission(permission))

export interface MedicineSubmissionAccess {
  canViewSurplus: boolean
  canManageSurplus: boolean
  canRemoveSurplus: boolean
  canViewNeedDrugs: boolean
  canManageNeedDrugs: boolean
  canRemoveNeedDrugs: boolean
  canViewNeedCategories: boolean
  canManageNeedCategories: boolean
  canCreateSmartSubmission: boolean
  canViewSmartSubmissions: boolean
  canAccessSurplus: boolean
  canAccessNeed: boolean
  canAccessPage: boolean
}

export const getMedicineSubmissionAccess = (): MedicineSubmissionAccess => {
  // ASP.NET Core may expose an Async action with or without the Async suffix,
  // depending on the action-name configuration. Supporting both keeps old roles valid.
  const canViewSurplus = hasAnyPermission([
    'pharmacydrug:allpharmacydrug',
    'pharmacydrug:allpharmacydrugasync',
  ])
  const canManageSurplus = hasPermission('pharmacydrug:save')
  const canRemoveSurplus = hasAnyPermission([
    'pharmacydrug:remove',
    'pharmacydrug:removemulti',
  ])

  const canViewNeedDrugs = hasPermission('favorite:getfavoritesdrug')
  const canManageNeedDrugs = hasPermission('favorite:save')
  const canRemoveNeedDrugs = hasPermission('favorite:remove')
  // The existing category-management component reads the combined favorites list.
  const canViewNeedCategories = hasPermission('favorite:getfavorites')
  const canManageNeedCategories = hasPermission('favorite:saveall')

  const canCreateSmartSubmission = hasPermission(
    'productsubmission:createsubmission',
  )
  const canViewSmartSubmissions = hasPermission(
    'productsubmission:getmysubmissions',
  )
  const hasSmartAccess = canCreateSmartSubmission || canViewSmartSubmissions

  const canAccessSurplus =
    canViewSurplus || canManageSurplus || canRemoveSurplus || hasSmartAccess
  const canAccessNeed =
    canViewNeedDrugs ||
    canManageNeedDrugs ||
    canRemoveNeedDrugs ||
    canViewNeedCategories ||
    canManageNeedCategories ||
    hasSmartAccess

  return {
    canViewSurplus,
    canManageSurplus,
    canRemoveSurplus,
    canViewNeedDrugs,
    canManageNeedDrugs,
    canRemoveNeedDrugs,
    canViewNeedCategories,
    canManageNeedCategories,
    canCreateSmartSubmission,
    canViewSmartSubmissions,
    canAccessSurplus,
    canAccessNeed,
    canAccessPage: canAccessSurplus || canAccessNeed,
  }
}
